import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { api, ApiException } from '@/api/client';

export function SettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['config'],
    queryFn: () => api<Record<string, unknown>>('/api/config'),
  });

  const [draft, setDraft] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const updateConfig = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api('/api/config', { method: 'PATCH', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['config'] });
      setError(null);
    },
    onError: (err) => {
      if (err instanceof ApiException) {
        setError(err.apiError.message);
      } else {
        setError('Failed to update config');
      }
    },
  });

  if (isLoading) {
    return (
      <div
        style={{
          height: 384,
          background: '#e2e8f0',
          borderRadius: 8,
          animation: 'pulse 2s infinite',
        }}
      />
    );
  }

  const text = draft || JSON.stringify(data ?? {}, null, 2);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Settings</h1>

      <div
        style={{
          padding: 16,
          background: '#fff',
          borderRadius: 8,
          border: '1px solid #e2e8f0',
        }}
      >
        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 12px 0' }}>
          Edit the LiteLLM config below. JSON format. Changes are saved to the proxy.
        </p>

        <textarea
          value={text}
          onChange={(e) => setDraft(e.target.value)}
          rows={20}
          style={{
            width: '100%',
            padding: 12,
            borderRadius: 6,
            border: '1px solid #cbd5e1',
            background: '#f8fafc',
            fontSize: 13,
            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          }}
        />

        {error && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background: '#fef2f2',
              color: '#b91c1c',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <Button
            onClick={() => {
              try {
                const parsed = JSON.parse(text);
                updateConfig.mutate(parsed);
              } catch {
                setError('Invalid JSON');
              }
            }}
            disabled={updateConfig.isPending}
          >
            {updateConfig.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button variant="outline" onClick={() => setDraft('')}>
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
}
